import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { LeadConversationForChat } from "@/lib/fetch-lead-conversation-for-chat";

const AGENT_BLOCKED_RESUME_TOAST = {
  title: "IA bloqueada neste estágio",
  description:
    "O lead está em um estágio configurado para desligar a IA. Mova o lead para outro estágio antes de reativar.",
  variant: "destructive" as const,
};

type PatchFn = (updater: (prev: LeadConversationForChat) => LeadConversationForChat) => void;

export function useConversationChatControls(args: {
  workspaceId: string | null;
  memberId: string | null;
  memberDisplayName: string | null;
  conversation: LeadConversationForChat | null;
  patchConversation: PatchFn;
}) {
  const { toast } = useToast();
  const [clearMessagesSignal, setClearMessagesSignal] = useState(0);
  const [clearContextDialogOpen, setClearContextDialogOpen] = useState(false);
  const [pendingClearConversationId, setPendingClearConversationId] = useState<string | null>(null);

  const handlePauseConversation = useCallback(
    async (conversationId: string) => {
      try {
        const { error } = await supabase.rpc("execute_human_handoff" as never, {
          _conversation_id: conversationId,
          _member_id: args.memberId ?? null,
          _reason: "manual_toggle",
        } as never);
        if (error) throw error;

        if (args.memberId && args.workspaceId) {
          await supabase.from("member_activity_log").insert({
            workspace_id: args.workspaceId,
            member_id: args.memberId,
            conversation_id: conversationId,
            action_type: "ai_disabled",
            metadata: { member_name: args.memberDisplayName ?? "Membro" },
          });
        }

        args.patchConversation((prev) => {
          if (prev.conversation_id !== conversationId) return prev;
          return { ...prev, state: "paused", followup_enabled: false };
        });
        toast({
          title: "Agente pausado",
          description: "O agente não enviará mais respostas automáticas.",
        });
      } catch {
        toast({
          title: "Erro ao pausar",
          description: "Não foi possível pausar o agente.",
          variant: "destructive",
        });
      }
    },
    [args, toast],
  );

  const handleResumeConversation = useCallback(
    async (conversationId: string, leadId?: string) => {
      const resolvedLeadId = leadId ?? args.conversation?.lead_id;
      if (resolvedLeadId) {
        const { data: blocked, error: blockError } = await supabase.rpc(
          "lead_has_agent_disabled_stage",
          { p_lead_id: resolvedLeadId },
        );
        if (!blockError && blocked === true) {
          toast(AGENT_BLOCKED_RESUME_TOAST);
          return;
        }
      }

      try {
        const { error } = await supabase.rpc("change_conversation_state" as never, {
          _conversation_id: conversationId,
          _new_state: "active",
          _reason: "manual_toggle",
          _actor_member_id: args.memberId ?? null,
          _enable_followup: true,
          _clear_escalated: true,
          _clear_pending_handoff: true,
        } as never);
        if (error) throw error;

        try {
          await supabase.rpc("clear_conversation_inbound_guard" as never, {
            p_conversation_id: conversationId,
            p_human_bypass_hours: 0,
          } as never);
        } catch {
          /* non-fatal */
        }

        if (args.memberId && args.workspaceId) {
          await supabase.from("member_activity_log").insert({
            workspace_id: args.workspaceId,
            member_id: args.memberId,
            conversation_id: conversationId,
            action_type: "ai_enabled",
            metadata: { member_name: args.memberDisplayName ?? "Membro" },
          });
        }

        args.patchConversation((prev) => {
          if (prev.conversation_id !== conversationId) return prev;
          return { ...prev, state: "active", followup_enabled: true };
        });
        toast({
          title: "Agente retomado",
          description: "O agente voltará a enviar respostas automáticas.",
        });
      } catch (error: unknown) {
        const message = String((error as { message?: string })?.message ?? "");
        if (message.includes("lead_stage_blocks_agent_resume")) {
          toast(AGENT_BLOCKED_RESUME_TOAST);
          return;
        }
        toast({
          title: "Erro ao retomar",
          description: "Não foi possível retomar o agente.",
          variant: "destructive",
        });
      }
    },
    [args, toast],
  );

  const handleCloseConversation = useCallback(
    async (conversationId: string) => {
      try {
        const { error } = await supabase
          .from("conversations")
          .update({
            is_closed: true,
            closed_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } as never)
          .eq("id", conversationId);
        if (error) throw error;

        args.patchConversation((prev) => {
          if (prev.conversation_id !== conversationId) return prev;
          return { ...prev, is_closed: true };
        });
        toast({ title: "Conversa encerrada" });
      } catch {
        toast({
          title: "Erro ao encerrar",
          description: "Não foi possível encerrar a conversa.",
          variant: "destructive",
        });
      }
    },
    [args, toast],
  );

  const handleToggleFollowup = useCallback(
    async (conversationId: string, currentStatus: boolean) => {
      try {
        const { error } = await supabase
          .from("conversations")
          .update({
            followup_enabled: !currentStatus,
            updated_at: new Date().toISOString(),
          })
          .eq("id", conversationId);
        if (error) throw error;

        args.patchConversation((prev) => {
          if (prev.conversation_id !== conversationId) return prev;
          return { ...prev, followup_enabled: !currentStatus };
        });
      } catch {
        toast({
          title: "Erro ao alterar follow-up",
          variant: "destructive",
        });
      }
    },
    [args, toast],
  );

  const handleClearContext = useCallback((conversationId: string) => {
    setPendingClearConversationId(conversationId);
    setClearContextDialogOpen(true);
  }, []);

  const confirmClearContext = useCallback(async () => {
    const conversationId = pendingClearConversationId;
    if (!conversationId) return;
    try {
      const { error } = await supabase.from("messages").delete().eq("conversation_id", conversationId);
      if (error) throw error;
      await supabase
        .from("conversations")
        .update({
          last_message_preview: null,
          last_message_at: null,
          last_message_direction: null,
          unread_count: 0,
          updated_at: new Date().toISOString(),
        })
        .eq("id", conversationId);
      setClearMessagesSignal((n) => n + 1);
      toast({ title: "Contexto limpo" });
    } catch {
      toast({
        title: "Erro ao limpar contexto",
        variant: "destructive",
      });
    } finally {
      setClearContextDialogOpen(false);
      setPendingClearConversationId(null);
    }
  }, [pendingClearConversationId, toast]);

  return {
    handlePauseConversation,
    handleResumeConversation,
    handleCloseConversation,
    handleToggleFollowup,
    handleClearContext,
    clearMessagesSignal,
    clearContextDialogOpen,
    setClearContextDialogOpen,
    confirmClearContext,
  };
}
